import { useTranslation } from 'react-i18next';

import { FCSecurePage } from '@/components/shared/navigation/securePage';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';
import EditorSidebar from '@/components/EditorSidebar';
import { DeleteConfirmationDialog } from '@/components';

import UserEditor from '@features/user-management/components/users/UserEditor';

import { Role } from '@core/types';
import { useAuthStore } from '@core/store';
import { userService, UserApiResponse } from '@/services/userService';

import { useUserListPage } from './hooks/useUserListPage';
import { UserListPage } from './components/users/UserListPage';
import { createOrUpdateUser } from './utils/userUtils';
import { useUserColumns } from './hooks/useUserColumns';

const createEmptyUser = (): UserApiResponse => ({
    givenName: '',
    surname: '',
    eMail: '',
    role: Role.User,
    organisationId: '',
    accountEnabled: true
});

const Users: FCSecurePage = () => {
    const { t } = useTranslation();
    const { hasRole } = useAuthStore();

    const page = useUserListPage(
        () => userService.getAllWithoutSuperAdminsForCurentOrganisation(),
        (id) => userService.getById(id),
        createOrUpdateUser,
        (id) => userService.delete(id)
    );

    const columns = useUserColumns({
        t,
        canEdit: (user) => !!user.role && hasRole(user.role),
        onEdit: page.handleSelect,
        onDelete: (user) => {
            page.setItemToDelete(user);
            page.setDeleteDialogOpen(true);
        }
    });

    return (
        <>
            <LoadingOverlay open={page.isSaving || page.isDeleting || page.loadingUser} />

            <UserListPage
                title={t('users')}
                routePath="/manage-users/users"
                users={page.users}
                loading={page.loading}
                columns={columns}
                lastUpdate={page.lastUpdate}
                sorting={{ defaultSortBy: 'givenName' }}
                onRefresh={page.load}
                onAdd={() => page.setSelected(createEmptyUser())}
            >
                <EditorSidebar open={!!page.selected} onClose={() => page.setSelected(null)}>
                    {page.selected && (
                        <UserEditor
                            item={page.selected}
                            onSave={page.handleSave}
                            onCancel={() => page.setSelected(null)}
                            onDelete={(u) => {
                                page.setItemToDelete(u);
                                page.setDeleteDialogOpen(true);
                            }}
                        />
                    )}
                </EditorSidebar>

                <DeleteConfirmationDialog
                    open={page.deleteDialogOpen}
                    heading={t('deleteHeading')}
                    message={t('deleteMessage', { item: t('user') })}
                    onConfirm={page.handleDelete}
                    onCancel={() => page.setDeleteDialogOpen(false)}
                />
            </UserListPage>
        </>
    );
};

export default Users;
